import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FunnelModal = ({ 
  isOpen, 
  onClose, 
  websiteId, 
  token, 
  onSuccess,
  editFunnel = null 
}) => {
  const [funnelName, setFunnelName] = useState('');
  const [steps, setSteps] = useState([
    { step_number: 1, step_type: 'pageview', page_path: '' },
    { step_number: 2, step_type: 'pageview', page_path: '' }
  ]);
  const [availablePaths, setAvailablePaths] = useState([]);
  const [pathStats, setPathStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPaths, setLoadingPaths] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchAvailablePaths();
      if (editFunnel) {
        setFunnelName(editFunnel.name);
        setSteps(editFunnel.steps.map((s, i) => ({
          step_number: i + 1,
          step_type: s.step_type || 'pageview',
          page_path: s.page_path
        })));
      } else {
        setFunnelName('');
        setSteps([
          { step_number: 1, step_type: 'pageview', page_path: '' },
          { step_number: 2, step_type: 'pageview', page_path: '' }
        ]);
      }
    }
  }, [isOpen, editFunnel]);

  const fetchAvailablePaths = async () => {
    setLoadingPaths(true);
    try {
      const res = await axios.get(`${API}/analytics/${websiteId}/available-paths`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailablePaths(res.data.paths || []);
      
      // Fetch visitor stats for each path
      await fetchPathStats(res.data.paths || []);
    } catch (err) {
      console.error('Failed to load paths:', err);
      setAvailablePaths(['/']);
    }
    setLoadingPaths(false);
  };

  const fetchPathStats = async (paths) => {
    try {
      // Get visitor counts for paths (simplified - using pageview counts)
      const stats = {};
      for (const path of paths.slice(0, 50)) { // Limit to first 50 paths
        const res = await axios.get(`${API}/analytics/${websiteId}/top-pages`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { period: '30d', limit: 100 }
        });
        
        if (res.data && res.data.pages) {
          res.data.pages.forEach(page => {
            stats[page.path] = page.visitors || page.count || 0;
          });
        }
        break; // Only need one call to get all page stats
      }
      setPathStats(stats);
    } catch (err) {
      console.error('Failed to load path stats:', err);
    }
  };

  const handleAddStep = () => {
    setSteps([
      ...steps,
      { step_number: steps.length + 1, step_type: 'pageview', page_path: '' }
    ]);
  };

  const handleRemoveStep = (index) => {
    if (steps.length <= 2) {
      toast.error('Funnel must have at least 2 steps');
      return;
    }
    const newSteps = steps.filter((_, i) => i !== index);
    // Re-number steps
    setSteps(newSteps.map((s, i) => ({ ...s, step_number: i + 1 })));
  };

  const handleStepChange = (index, field, value) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setSteps(newSteps);
    setActiveDropdown(null);
  };

  const calculateDropoff = (currentIndex) => {
    if (currentIndex === 0) return null;
    const currentVisitors = pathStats[steps[currentIndex]?.page_path] || 0;
    const previousVisitors = pathStats[steps[currentIndex - 1]?.page_path] || 0;
    
    if (previousVisitors === 0) return null;
    return ((1 - (currentVisitors / previousVisitors)) * 100).toFixed(2);
  };

  const calculateConversionRate = () => {
    const firstStepVisitors = pathStats[steps[0]?.page_path] || 0;
    const lastStepVisitors = pathStats[steps[steps.length - 1]?.page_path] || 0;
    
    if (firstStepVisitors === 0) return '0.00';
    return ((lastStepVisitors / firstStepVisitors) * 100).toFixed(2);
  };

  const handleSubmit = async () => {
    if (!funnelName.trim()) {
      toast.error('Please enter a funnel name');
      return;
    }

    const filledSteps = steps.filter(s => s.page_path);
    if (filledSteps.length < 2) {
      toast.error('Please add at least 2 steps with paths');
      return;
    }

    setLoading(true);
    try {
      if (editFunnel) {
        await axios.put(
          `${API}/funnels/${editFunnel.id}`,
          { name: funnelName, steps: filledSteps },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Funnel updated successfully');
      } else {
        await axios.post(
          `${API}/funnels`,
          { website_id: websiteId, name: funnelName, steps: filledSteps },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Funnel created successfully');
      }
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save funnel');
    }
    setLoading(false);
  };

  const filteredPaths = availablePaths.filter(path => 
    path.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {editFunnel ? 'Edit funnel' : 'Add funnel'}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
          {/* Funnel Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Funnel name
            </label>
            <Input
              type="text"
              value={funnelName}
              onChange={(e) => setFunnelName(e.target.value)}
              placeholder="e.g. From Blog to Purchase"
              className="w-full"
            />
          </div>

          {/* Funnel Steps */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Funnel steps
            </label>
            
            <div className="space-y-3">
              {steps.map((step, index) => (
                <div key={index} className="flex items-center gap-2">
                  {/* Path Dropdown */}
                  <div className="relative flex-1">
                    <button
                      type="button"
                      onClick={() => setActiveDropdown(activeDropdown === index ? null : index)}
                      className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white text-left hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <span className={step.page_path ? 'text-gray-900' : 'text-gray-400'}>
                        {step.page_path ? `Visit ${step.page_path}` : 'Select option or search by typing'}
                      </span>
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    </button>

                    {/* Dropdown Menu */}
                    {activeDropdown === index && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                        <div className="p-2 border-b">
                          <Input
                            type="text"
                            placeholder="Search paths..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full text-sm"
                            autoFocus
                          />
                        </div>
                        {loadingPaths ? (
                          <div className="p-3 text-center text-gray-500">Loading...</div>
                        ) : (
                          <div className="py-1">
                            {filteredPaths.length === 0 ? (
                              <div className="p-3 text-center text-gray-500">No paths found</div>
                            ) : (
                              filteredPaths.slice(0, 20).map((path) => (
                                <button
                                  key={path}
                                  type="button"
                                  onClick={() => {
                                    handleStepChange(index, 'page_path', path);
                                    setSearchQuery('');
                                  }}
                                  className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center justify-between"
                                >
                                  <span className="text-gray-900 text-sm">Visit {path}</span>
                                  {pathStats[path] !== undefined && (
                                    <span className="text-gray-500 text-xs">
                                      {pathStats[path]} visitors
                                    </span>
                                  )}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Delete Button */}
                  <button
                    type="button"
                    onClick={() => handleRemoveStep(index)}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>

                  {/* Stats */}
                  <div className="w-28 text-right text-sm">
                    {step.page_path && pathStats[step.page_path] !== undefined && (
                      <span className="text-gray-500">
                        Visitors: {pathStats[step.page_path]}
                      </span>
                    )}
                    {index > 0 && step.page_path && calculateDropoff(index) !== null && (
                      <div className="text-red-500 text-xs">
                        Dropoff: {calculateDropoff(index)}%
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Add Step Button */}
            <button
              type="button"
              onClick={handleAddStep}
              className="mt-3 text-indigo-600 hover:text-indigo-700 text-sm font-medium flex items-center"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add another step
            </button>
          </div>

          {/* Conversion Rate */}
          {steps.filter(s => s.page_path).length >= 2 && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">
                Last month conversion rate: <strong>{calculateConversionRate()}%</strong>
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <Button
            onClick={handleSubmit}
            disabled={loading || !funnelName.trim() || steps.filter(s => s.page_path).length < 2}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {loading ? 'Saving...' : editFunnel ? 'Update funnel' : 'Add funnel'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FunnelModal;
